/**
 * S10 图鉴系统 —— 实现 design/gdd/systems/S10-codex.md。
 *
 * CodexStore 单例：定义星球 ID 空间、解锁规则（通关解锁目标色）、收集界面数据。
 * 不含：存档持久化（→ S9，只经 S9 API / 事件）、设置项（→ S12）、分享（→ S11）、局内编排（→ S7）。
 */

import { SaveStore } from './save';
import { EventBus } from './bus';
import type { PieceColor } from './types';

export interface CodexEntry {
  planetId: string;
  name: string;
  gemName: string;
  copy: string;
  lockedHint: string;
}

/** S10 §2.1 / §2.6：六颗主棋（顺序 = 太阳系由内向外） */
const PLANETS: CodexEntry[] = [
  {
    planetId: 'mercury', name: '水星', gemName: '白水晶',
    copy: '我是水星，离太阳最近的孩子。别嫌我灰扑扑的——这一身坑，都是几十亿年挨撞的勋章。我跑得最快，绕太阳一圈只要八十八天。',
    lockedHint: '继续通关，收集更多星球',
  },
  {
    planetId: 'venus', name: '金星', gemName: '琥珀',
    copy: '我是金星，夜空里最亮的行星，古时候的人们叫我启明星。厚厚的云被裹住全身，谁也别想一眼看透。我还偏爱倒着转，够叛逆吧？',
    lockedHint: '继续通关，收集更多星球',
  },
  {
    planetId: 'earth', name: '地球', gemName: '海蓝宝',
    copy: '我是地球，目前已知唯一有生命的星球。蓝的是海，绿的是陆，白云天天在我脸上逛。别忘了——你现在，就站在我身上玩呢。',
    lockedHint: '继续通关，收集更多星球',
  },
  {
    planetId: 'mars', name: '火星', gemName: '红玉髓',
    copy: '我是火星，一身铁锈红。头顶那顶白帽子，是两极的冰冠。我的沙尘暴一发威，能连刮几个月不停。',
    lockedHint: '继续通关，收集更多星球',
  },
  {
    planetId: 'saturn', name: '土星', gemName: '猫眼石',
    copy: '我是土星，戴着全太阳系最风光的环。那圈环其实是数不清的冰块和碎石，我走到哪儿，它们就跟到哪儿。',
    lockedHint: '继续通关，收集更多星球',
  },
  {
    planetId: 'neptune', name: '海王星', gemName: '坦桑石',
    copy: '我是海王星，住在太阳系最远的角落。别看我安安静静的——我身上的风，是全太阳系最快的，谁也追不上。',
    lockedHint: '继续通关，收集更多星球',
  },
];

const ORDER = ['mercury', 'venus', 'earth', 'mars', 'saturn', 'neptune'];

export class CodexStore {
  private static _inst: CodexStore | null = null;
  static get instance(): CodexStore {
    if (!CodexStore._inst) CodexStore._inst = new CodexStore();
    return CodexStore._inst;
  }

  private save = SaveStore.instance;
  /** 局中被动记账：本关目标色（S10 §④，内存态不落盘） */
  private lastGoalColor: PieceColor | null = null;
  /** 元事件总线：CodexUnlocked / CodexViewed（S7 CODEX_REVEAL 消费侧；S9 经 main 接线） */
  readonly bus = new EventBus();

  /** S10 §④：LevelStarted 被动记账（零 UI / 零表现） */
  handleLevelStarted(evt: { goals?: { type: string; color?: PieceColor }[] }): void {
    const g = evt.goals?.[0];
    if (g && g.type === 'ELIMINATE_COLOR' && g.color) {
      this.lastGoalColor = g.color;
    } else {
      this.lastGoalColor = null; // E3：多目标 / 脏数据不猜色
    }
  }

  /** S10 §④：LevelComplete 解锁判定（幂等） */
  handleLevelComplete(_evt: unknown): void {
    if (!this.lastGoalColor) {
      console.warn('[codex] LevelComplete 无对应 LevelStarted 目标色，跳过解锁');
      return;
    }
    const pid = this.lastGoalColor.toLowerCase();
    if (!this.save.getCodex().unlocked.includes(pid)) {
      this.bus.emit('CodexUnlocked', { planetId: pid }); // → S9 追加指针 + hasNew；S7 CODEX_REVEAL
    }
  }

  /** S10 §2.3：打开图鉴界面 → 发 CodexViewed（消费 hasNew） */
  openView(): void {
    this.bus.emit('CodexViewed', {}); // → S9 置 hasNew=false
  }

  getEntries(): { entry: CodexEntry; unlocked: boolean }[] {
    const unlocked = this.save.getCodex().unlocked;
    return ORDER.map((id) => {
      const entry = PLANETS.find((p) => p.planetId === id);
      // E4 前向兼容：未知 ID 不会出现于 6 格（ORDER 固定 6）
      return { entry: entry as CodexEntry, unlocked: unlocked.includes(id) };
    });
  }

  getEntry(planetId: string): CodexEntry | null {
    return PLANETS.find((p) => p.planetId === planetId) ?? null;
  }

  /** n/6 进度（只数已知 ID，E4 前向兼容） */
  getUnlockedCount(): number {
    const unlocked = this.save.getCodex().unlocked;
    return ORDER.filter((id) => unlocked.includes(id)).length;
  }

  hasNew(): boolean {
    return this.save.getCodex().hasNew;
  }
}
